import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ORGANIZATIONS_LOGO_URL } from 'shared/constants';
import { Loader } from '@/components/loader';
import { Separator } from '@/components/ui/separator';
import DetailsBox from './components/details-box';
import FeaturesBox from './components/features-box';
import { useOrganizationContext } from './organization.context';
import UploadLogoBox from './components/upload-logo';
import UploadUsers from './components/upload-users';
import UploadRotationSlots from './components/upload-rotation-slots';
import UploadQualityMetrics from './components/upload-quality-metrics';
import HubDetails from './components/hub-details';
import NotificationSettings from './components/notification-settings';
import ConnectionSettings from './components/connection-settings';
import BiometricsSettings from './components/biometrics-settings';
import PolicyTechSettings from './components/policytech-settings';
import WidgetsBox from './components/widgets-box';
import UploadKnowledgeManagement from './components/auto-uploads/upload-knowledge-management';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import HubShareAll from './components/hub-share-all';
import SearchRankingCard from './components/search-ranking/search-ranking-card';
import QualityMetricsCard from './components/quality-metrics/quality-metrics-card';

export default function Organization() {
  const { id } = useParams();
  const { setOrganizationId, organization, loading } = useOrganizationContext();

  useEffect(() => {
    setOrganizationId(id);
  }, [id, setOrganizationId]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex flex-col space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">{organization.name}</h2>
          <p className="text-muted-foreground text-xs">{organization.id}</p>
        </div>
        <div className="p-3 mr-4 rounded-md" style={{ background: '#4c5675' }}>
          <img
            src={`${ORGANIZATIONS_LOGO_URL}web/${organization.logo}`}
            alt="logo"
            width={250}
            height={50}
            className="object-contain"
          />
        </div>
      </div>
      <Separator className="my-4" />

      <div className="justify-center gap-6 rounded-lg flex flex-col md:grid md:grid-cols-2 xl:grid-cols-3">
        <div className="flex flex-col gap-6 w-full [&>:last-child]:flex-1">
          <DetailsBox />
          <UploadLogoBox />
          <UploadKnowledgeManagement />
        </div>

        <div className="flex flex-col gap-6 w-full [&>:last-child]:flex-1">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/2 [&>*]:h-full">
              <Card className="flex flex-col items-center">
                <CardHeader>
                  <CardTitle>Upload CSV data</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 flex flex-col">
                  <UploadUsers />

                  <UploadRotationSlots />

                  <UploadQualityMetrics />
                </CardContent>
              </Card>
            </div>
            <div className="w-full md:w-1/2 [&>*]:h-full">
              <HubShareAll />
            </div>
          </div>
          <NotificationSettings />
        </div>

        <div className="flex flex-col gap-6 w-full md:col-span-2 xl:col-span-1 [&>:last-child]:flex-1">
          <HubDetails />
        </div>

        <div className="md:col-span-2 xl:col-span-3 w-full">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-1/2 [&>*]:h-full">
                <FeaturesBox />
              </div>
              <div className="w-full md:w-1/2 [&>*]:h-full">
                <WidgetsBox />
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-1/2 [&>*]:h-full">
                <ConnectionSettings />
              </div>
              <div className="w-full md:w-1/2 [&>*]:h-full">
                <BiometricsSettings />
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-1/2 [&>*]:h-full">
                <SearchRankingCard />
              </div>
              <div className="w-full md:w-1/2 [&>*]:h-full">
                <QualityMetricsCard />
              </div>
            </div>
            <div className="w-full [&>*]:h-full">
              <PolicyTechSettings />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
